'use client'

import Autoplay from 'embla-carousel-autoplay'
import useEmblaCarousel from 'embla-carousel-react'
import {
  ComponentProps,
  PropsWithChildren,
  useCallback,
  useEffect,
  type KeyboardEvent,
} from 'react'

import { cn } from '@/shared/lib'

import { CarouselContext, CarouselProps, useCarousel } from './lib/use-carousel'
import { useScroll } from './lib/use-scroll'

type RootProps = CarouselProps & {
  className?: string
  useAutoPlay?: boolean
}

export const Provider = ({
  orientation = 'horizontal',
  options,
  setApi,
  plugins,
  useAutoPlay = false,
  className,
  children,
}: PropsWithChildren<RootProps>) => {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...options,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    [...(plugins || []), ...(useAutoPlay ? [Autoplay({ delay: 5000 })] : [])],
  )

  const {
    selectedIndex,
    scrollSnaps,
    canScrollPrev,
    canScrollNext,
    handleInit,
    handleSelect,
    scrollPrev,
    scrollNext,
    scrollTo,
  } = useScroll(api)

  useEffect(() => {
    if (!api || !setApi) return
    setApi(api)
  }, [api, setApi])

  useEffect(() => {
    if (!api) return

    handleInit(api)
    handleSelect(api)
    api.on('reInit', handleInit)
    api.on('reInit', handleSelect)
    api.on('select', handleSelect)

    return () => {
      api?.off('select', handleSelect)
    }
  }, [api, handleInit, handleSelect])

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        options,
        orientation:
          orientation || (options?.axis === 'y' ? 'vertical' : 'horizontal'),
        selectedIndex,
        scrollSnaps,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
        scrollTo,
      }}
    >
      <div className={cn('relative', className)}>{children}</div>
    </CarouselContext.Provider>
  )
}

export const Root = ({
  children,
  className,
  ...restProps
}: ComponentProps<'div'>) => {
  const { scrollPrev, scrollNext } = useCarousel()

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        scrollPrev()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        scrollNext()
      }
    },
    [scrollPrev, scrollNext],
  )

  return (
    <div
      onKeyDownCapture={handleKeyDown}
      className={cn('relative', className)}
      data-slot="carousel"
      role="region"
      aria-roledescription="carousel"
      {...restProps}
    >
      {children}
    </div>
  )
}

export const Content = ({ className, ...props }: ComponentProps<'div'>) => {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div
      ref={carouselRef}
      className="overflow-hidden"
      data-slot="carousel-content"
    >
      <div
        className={cn(
          'flex',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className,
        )}
        {...props}
      />
    </div>
  )
}

export const Item = ({ className, ...props }: ComponentProps<'div'>) => {
  const { orientation } = useCarousel()

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className,
      )}
      {...props}
    />
  )
}
